import { useState, useEffect } from "react";
import { Smartphone, X, Download, Share } from "lucide-react";

// Bottom-sheet prompt shown on mobile BROWSERS nudging the user into the app:
// - Android browser → download the signed APK (/downloads/android)
// - iOS Safari      → Add to Home Screen (no App Store build; PWA install)
// Hidden when: not a phone, already running installed (standalone/TWA), or the
// user dismissed it (persisted 14 days).
const DISMISS_KEY = "midtc_app_prompt_dismissed_until";
const API_URL = process.env.REACT_APP_API_URL || "";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true; // iOS
}
function inTwa() {
  // The combined Android app launches the site with ?midtc_native_id=… and runs
  // as an installed app — either signal means "already in the app".
  return new URLSearchParams(window.location.search).has("midtc_native_id")
      || document.referrer.startsWith("android-app://");
}

export default function MobileAppPrompt() {
  const [show, setShow] = useState(false);
  const ua = navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);

  useEffect(() => {
    if (!(isIOS || isAndroid)) return;
    if (isStandalone() || inTwa()) return;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < until) return;
    // Brief delay so it doesn't fight the first paint / login.
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, [isIOS, isAndroid]);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 14 * 86400_000));
    setShow(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:3000, display:"flex", alignItems:"flex-end",
      background:"rgba(2,6,23,0.55)", backdropFilter:"blur(2px)" }} onClick={dismiss}>
      <div onClick={e => e.stopPropagation()} style={{
        width:"100%", background:"#111b2f", borderTopLeftRadius:18, borderTopRightRadius:18,
        borderTop:"1px solid rgba(79,111,255,0.3)", padding:"20px 20px calc(20px + env(safe-area-inset-bottom))",
        boxShadow:"0 -12px 40px rgba(0,0,0,0.5)", animation:"fadeIn 0.25s ease-out",
      }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
          <div style={{ width:46, height:46, borderRadius:12, flexShrink:0, overflow:"hidden",
            background:"linear-gradient(135deg,#4f6fff,#7b61ff)", display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:"0 0 18px rgba(79,111,255,0.4)" }}>
            <Smartphone size={22} color="#fff" />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>Get the MIDTC app</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)", marginTop:3, lineHeight:1.5 }}>
              {isAndroid
                ? "Install the app for push notifications and to let us manage and support this device."
                : "Add MIDTC to your Home Screen for a full-screen app with push notifications."}
            </div>
          </div>
          <button onClick={dismiss} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:4, flexShrink:0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginTop:16 }}>
          {isAndroid ? (
            <a href={`${API_URL}/downloads/android`} onClick={dismiss}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10,
                padding:"13px", borderRadius:10, textDecoration:"none",
                background:"linear-gradient(135deg,#4f6fff,#7b61ff)", color:"#fff", fontSize:14, fontWeight:700,
                boxShadow:"0 0 20px rgba(79,111,255,0.35)" }}>
              <Download size={17} /> Download the app
            </a>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", borderRadius:10,
              background:"rgba(79,111,255,0.1)", border:"1px solid rgba(79,111,255,0.25)", color:"rgba(255,255,255,0.85)", fontSize:13 }}>
              <Share size={18} color="#818cf8" style={{ flexShrink:0 }} />
              <span>Tap the <strong>Share</strong> icon below, then <strong>“Add to Home Screen”</strong>.</span>
            </div>
          )}
        </div>

        <button onClick={dismiss} style={{ display:"block", margin:"14px auto 0", background:"none", border:"none",
          color:"rgba(255,255,255,0.4)", fontSize:12, cursor:"pointer" }}>
          Not now
        </button>
      </div>
    </div>
  );
}
